/**
 * LeagueDetailPage — `/leagues/:id` — single league view.
 *
 * Three panes:
 *   1. Header — league name, member count, country-lock badge, "just created"
 *      banner with copy-able invite code if arriving from create.
 *   2. Today's leaderboard — members ranked, dim those who haven't played.
 *      Highlights the current user's row.
 *   3. Actions — share code (copy + Web Share), leave (members) / delete
 *      (creators), back to leagues list.
 *
 * Auto-refreshes leaderboard once a minute so during the World Cup window
 * the page feels alive when friends are taking kicks. Stops polling when
 * the tab is hidden.
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ApiError, api, type LeagueSummary, type LeagueLeaderboardResponse } from "@/api/client";
import { useAuthStore } from "@/store/authStore";
import { flag, flagUrl, resolveCountry } from "@/lib/countries";

function copyToClipboard(text: string): Promise<boolean> {
  if (!navigator.clipboard) return Promise.resolve(false);
  return navigator.clipboard.writeText(text).then(
    () => true,
    () => false,
  );
}

export function LeagueDetailPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const status = useAuthStore((s) => s.status);

  const outcomeLabel = (o: string | null): string => {
    if (!o) return "—";
    switch (o) {
      case "GOAL": return t("league_detail.outcome_goal");
      case "SAVED": return t("league_detail.outcome_saved");
      case "POST": return t("league_detail.outcome_post");
      case "OVER": return t("league_detail.outcome_over");
      case "WIDE": return t("league_detail.outcome_wide");
      case "WALL": return t("league_detail.outcome_wall");
      default: return o;
    }
  };

  const [searchParams] = useSearchParams();
  const justCreated = searchParams.get("just_created") === "1";

  const [league, setLeague] = useState<LeagueSummary | null>(null);
  const [board, setBoard] = useState<LeagueLeaderboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copyToast, setCopyToast] = useState<string | null>(null);

  // Initial fetch + 60s polling while tab is visible.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.getLeague(id);
        if (cancelled) return;
        setLeague(r.league);
        if (r.isMember) {
          const b = await api.leagueLeaderboard(id);
          if (!cancelled) setBoard(b);
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setError(t("league_detail.league_not_found"));
        } else if (err instanceof ApiError && err.status === 401) {
          setError(t("league_detail.signin_to_view"));
        } else {
          setError(t("league_detail.load_failed"));
        }
      }
    };
    void load();
    const poll = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
    };
  }, [id, status, user?.id]);

  // Auto-clear copy toast.
  useEffect(() => {
    if (!copyToast) return;
    const t = window.setTimeout(() => setCopyToast(null), 2200);
    return () => window.clearTimeout(t);
  }, [copyToast]);

  const onCopy = async (text: string, kind: "code" | "link") => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopyToast(
        kind === "code"
          ? t("league_detail.code_copied")
          : t("league_detail.invite_link_copied"),
      );
    } else {
      setCopyToast(t("league_detail.copy_failed"));
    }
  };

  const onShareCode = async () => {
    if (!league) return;
    const joinUrl = `https://www.curvle.world/league/${league.code}`;
    const text = t("league_detail.share_text", { name: league.name, url: joinUrl });
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: t("league_detail.share_title"), text, url: joinUrl });
        return;
      } catch (err) {
        // User dismissed or share unavailable — fall back to copy.
        if (err instanceof Error && err.name === "AbortError") return;
      }
    }
    void onCopy(text, "link");
  };

  const onLeave = async () => {
    if (!league || busy) return;
    const ok = window.confirm(t("league_detail.leave_confirm", { name: league.name }));
    if (!ok) return;
    setBusy(true);
    try {
      await api.leaveLeague(league.id);
      navigate("/leagues");
    } catch {
      setError(t("league_detail.leave_failed"));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!league || busy) return;
    const ok = window.confirm(t("league_detail.delete_confirm", { name: league.name }));
    if (!ok) return;
    setBusy(true);
    try {
      await api.deleteLeague(league.id);
      navigate("/leagues");
    } catch {
      setError(t("league_detail.delete_failed"));
    } finally {
      setBusy(false);
    }
  };

  // -- Render ---------------------------------------------------------------

  if (error) {
    return (
      <Shell>
        <p className="text-sm text-rose-300">{error}</p>
        <Link
          to="/leagues"
          className="mt-3 inline-block rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20"
        >
          {t("league_detail.back_to_leagues")}
        </Link>
      </Shell>
    );
  }

  if (!league) {
    return (
      <Shell>
        <p className="text-sm opacity-70">{t("league_detail.loading")}</p>
      </Shell>
    );
  }

  const isCreator = league.myRole === "creator";
  const isMember = !!league.myRole;
  const joinUrl = `https://www.curvle.world/league/${league.code}`;

  return (
    <Shell>
      {/* Just-created banner */}
      {justCreated && isCreator && (
        <div className="mb-5 rounded-2xl bg-amber-300/15 p-4 ring-1 ring-amber-300/40">
          <p className="text-sm font-bold text-amber-200">{t("league_detail.just_created_title")}</p>
          <p className="mt-1 text-xs opacity-80">
            {t("league_detail.just_created_body")}
          </p>
        </div>
      )}

      {/* Header */}
      <div className="rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-bold">{league.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs opacity-70">
              <span>{t("league_detail.members_count", { current: league.memberCount, max: league.maxMembers })}</span>
              {league.countryLock && (
                <span className="rounded-full bg-white/10 px-2 py-0.5">
                  {flag(league.countryLock)} {t("league_detail.country_only", { country: resolveCountry(league.countryLock).name })}
                </span>
              )}
              {isCreator && (
                <span className="rounded-full bg-amber-300/20 px-2 py-0.5 text-amber-200">
                  {t("league_detail.you_created")}
                </span>
              )}
            </div>
          </div>
          <Link
            to="/leagues"
            className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20"
          >
            {t("league_detail.all_leagues")}
          </Link>
        </div>

        {/* Invite code panel */}
        {isMember && (
          <div className="mt-4 rounded-xl bg-emerald-900/40 p-3 ring-1 ring-emerald-300/20">
            <p className="text-[11px] font-semibold uppercase tracking-wider opacity-70">
              {t("league_detail.invite_code_label")}
            </p>
            <div className="mt-1 flex items-center justify-between gap-2">
              <p className="font-mono text-2xl font-bold tracking-widest text-amber-200">
                {league.code}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onCopy(league.code, "code")}
                  className="rounded-full bg-white/10 px-3 py-1.5 text-xs hover:bg-white/20"
                >
                  {t("league_detail.copy_code")}
                </button>
                <button
                  type="button"
                  onClick={onShareCode}
                  className="rounded-full bg-amber-300 px-3 py-1.5 text-xs font-semibold text-emerald-950 hover:bg-amber-200"
                >
                  {t("league_detail.share")}
                </button>
              </div>
            </div>
            <p className="mt-2 truncate text-[11px] opacity-60">{joinUrl}</p>
          </div>
        )}
      </div>

      {/* Leaderboard */}
      {isMember && board && (
        <>
          <h2 className="mt-6 text-sm font-semibold uppercase tracking-wider opacity-70">
            {t("league_detail.today_prefix")} &middot; {formatDateLabel(board.date)}
          </h2>
          {board.rows.length === 0 ? (
            <p className="mt-3 text-sm opacity-60">{t("league_detail.no_members_yet")}</p>
          ) : (
            <ul className="mt-3 space-y-1.5">
              {board.rows.map((r) => (
                <li
                  key={r.userId}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                    r.isMe
                      ? "bg-amber-300/10 ring-1 ring-amber-300/30"
                      : r.hasPlayed
                      ? "bg-white/5"
                      : "bg-white/5 opacity-50"
                  }`}
                >
                  <span className="w-7 text-right text-sm font-bold opacity-70">
                    {r.rank ?? "—"}
                  </span>
                  {flagUrl(r.countryCode, 40) ? (
                    <img
                      src={flagUrl(r.countryCode, 40)!}
                      width={20}
                      height={15}
                      alt=""
                      className="rounded-sm shadow-[0_0_0_1px_rgba(255,255,255,0.1)]"
                    />
                  ) : (
                    <span className="text-base">🌐</span>
                  )}
                  <span className="min-w-0 flex-1 truncate">
                    {r.displayName}
                    {r.isMe && <span className="ml-1 text-xs opacity-60">{t("league_detail.you_suffix")}</span>}
                    {r.role === "creator" && (
                      <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-200/80">
                        {t("league_detail.creator_badge")}
                      </span>
                    )}
                    {r.isPro && (
                      <span className="ml-1 rounded-full bg-amber-300/20 px-1 text-[10px] font-bold text-amber-200">
                        PRO
                      </span>
                    )}
                  </span>
                  <span className="text-xs opacity-60">{outcomeLabel(r.outcome)}</span>
                  <span className="w-12 text-right font-bold text-amber-200">
                    {r.hasPlayed ? r.score : "—"}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {!board.me?.hasPlayed && (
            <Link
              to="/play"
              className="mt-4 inline-block rounded-full bg-amber-300 px-5 py-2.5 font-bold text-emerald-950 hover:bg-amber-200"
            >
              {t("league_detail.take_kick")}
            </Link>
          )}
        </>
      )}

      {/* Non-member view */}
      {!isMember && (
        <div className="mt-6 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
          <p className="text-sm opacity-80">
            {t("league_detail.non_member_body", { code: league.code })}
          </p>
        </div>
      )}

      {/* Footer actions */}
      {isMember && (
        <div className="mt-6 flex flex-wrap gap-2">
          {isCreator ? (
            <button
              type="button"
              disabled={busy}
              onClick={onDelete}
              className="rounded-full bg-rose-900/50 px-4 py-2 text-xs font-semibold text-rose-200 hover:bg-rose-900/70 disabled:opacity-50"
            >
              {t("league_detail.delete")}
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={onLeave}
              className="rounded-full bg-white/5 px-4 py-2 text-xs hover:bg-white/10 disabled:opacity-50"
            >
              {t("league_detail.leave")}
            </button>
          )}
        </div>
      )}

      {copyToast && (
        <div
          className="pointer-events-none fixed left-1/2 bottom-6 z-40 -translate-x-1/2 rounded-full bg-emerald-700 px-4 py-1.5 text-sm font-semibold shadow-lg"
          role="status"
          aria-live="polite"
        >
          {copyToast}
        </div>
      )}
    </Shell>
  );
}

function formatDateLabel(iso: string): string {
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-emerald-950 text-white">
      <header className="border-b border-white/10 px-4 py-3">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <Link to="/" className="text-sm font-bold tracking-wider opacity-80 hover:opacity-100">
            CURVLE
          </Link>
          <Link
            to="/play"
            className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20"
          >
            Today&rsquo;s kick →
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-6">{children}</main>
    </div>
  );
}
